/**
 * RestTimer — Countdown timer for rest between sets.
 * Features: quick-select times, start/pause/reset, haptic on completion, minimizable.
 */

import { useState, useEffect, useRef, useCallback, useImperativeHandle, forwardRef } from 'react';
import {
  View,
  Text,
  StyleSheet,
  TouchableOpacity,
  Animated,
  Vibration,
  Platform,
  Modal,
  Pressable,
} from 'react-native';
import { useTheme } from '@/contexts/ThemeContext';
import { hapticsUtil } from '@/lib/haptics';
import { Play, Pause, RotateCcw, X, Timer, Minus } from 'lucide-react-native';

interface RestTimerProps {
  isVisible: boolean;
  onDismiss: () => void;
  onComplete?: () => void;
  defaultSeconds?: number;
  autoStart?: boolean;
}

const QUICK_TIMES = [30, 60, 90, 120, 180, 300];

function formatTime(secs: number): string {
  const m = Math.floor(secs / 60);
  const s = secs % 60;
  return `${m}:${s.toString().padStart(2, '0')}`;
}

export default function RestTimer({
  isVisible,
  onDismiss,
  onComplete,
  defaultSeconds = 90,
  autoStart = false,
}: RestTimerProps) {
  const { theme } = useTheme();
  const c = theme.colors;
  const [totalSeconds, setTotalSeconds] = useState(defaultSeconds);
  const [remaining, setRemaining] = useState(defaultSeconds);
  const [isRunning, setIsRunning] = useState(false);
  const [isMinimized, setIsMinimized] = useState(false);
  const [showGetIt, setShowGetIt] = useState(false);
  const getItAnim = useRef(new Animated.Value(0)).current;
  const intervalRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const pulseAnim = useRef(new Animated.Value(1)).current;

  // Reset timer when shown, but do NOT auto-start — user taps the play button
  useEffect(() => {
    if (isVisible && !isRunning) {
      setRemaining(totalSeconds);
      setIsMinimized(false);
    }
  }, [isVisible]); // eslint-disable-line react-hooks/exhaustive-deps

  // Countdown logic
  useEffect(() => {
    if (isRunning && remaining > 0) {
      intervalRef.current = setInterval(() => {
        setRemaining(prev => {
          if (prev <= 1) {
            setIsRunning(false);
            setIsMinimized(false); // Re-open modal when done
            // Completion feedback
            if (Platform.OS !== 'web') {
              hapticsUtil.workoutSaved();
              Vibration.vibrate([0, 200, 100, 200]);
            }
            onComplete?.();
            // Show "GET IT!" flash
            setShowGetIt(true);
            getItAnim.setValue(0);
            Animated.sequence([
              Animated.timing(getItAnim, { toValue: 1, duration: 200, useNativeDriver: true }),
              Animated.delay(1500),
              Animated.timing(getItAnim, { toValue: 0, duration: 400, useNativeDriver: true }),
            ]).start(() => setShowGetIt(false));
            // Pulse animation on complete
            Animated.sequence([
              Animated.timing(pulseAnim, { toValue: 1.05, duration: 150, useNativeDriver: true }),
              Animated.timing(pulseAnim, { toValue: 1, duration: 150, useNativeDriver: true }),
            ]).start();
            return 0;
          }
          return prev - 1;
        });
      }, 1000);
    }
    return () => {
      if (intervalRef.current) clearInterval(intervalRef.current);
    };
  }, [isRunning, remaining]);

  const progress = totalSeconds > 0 ? remaining / totalSeconds : 0;
  const progressColor = remaining === 0 ? c.success : remaining <= 10 ? c.warning : c.accent;

  const handleStart = () => {
    if (remaining === 0) setRemaining(totalSeconds);
    setIsRunning(true);
    // Auto-minimize so user can navigate the app while timer runs
    setIsMinimized(true);
  };
  const handlePause = () => setIsRunning(false);
  const handleReset = () => {
    setIsRunning(false);
    setRemaining(totalSeconds);
  };
  const handleSetTime = (secs: number) => {
    setTotalSeconds(secs);
    setRemaining(secs);
    setIsRunning(false);
  };

  if (!isVisible) return null;

  // Minimized floating pill
  if (isMinimized) {
    return (
      <Modal visible transparent animationType="none" onRequestClose={onDismiss}>
        <View style={styles.minimizedOverlay} pointerEvents="box-none">
          <TouchableOpacity
            style={[styles.minimizedPill, { backgroundColor: c.accent, borderColor: c.border }]}
            onPress={() => setIsMinimized(false)}
            activeOpacity={0.8}
          >
            <Timer size={14} color={c.accentForeground} />
            <Text style={[styles.minimizedText, { color: c.accentForeground }]}>
              {formatTime(remaining)}
            </Text>
          </TouchableOpacity>
        </View>
      </Modal>
    );
  }

  return (
    <Modal visible transparent animationType="fade" onRequestClose={onDismiss}>
    <View style={styles.overlay} pointerEvents="box-none">
    <Animated.View
      style={[
        styles.container,
        { backgroundColor: c.surface, borderColor: c.border, transform: [{ scale: pulseAnim }] },
      ]}
    >
      {/* Header */}
      <View style={styles.header}>
        <TouchableOpacity onPress={() => setIsMinimized(true)} style={styles.headerBtn}>
          <Minus size={18} color={c.textTertiary} />
        </TouchableOpacity>
        <Text style={[styles.title, { color: c.text }]}>Rest Timer</Text>
        <TouchableOpacity onPress={onDismiss} style={styles.headerBtn}>
          <X size={18} color={c.textTertiary} />
        </TouchableOpacity>
      </View>

      {/* Timer display */}
      <View style={styles.timerDisplay}>
        {/* Progress ring background */}
        <View style={[styles.ringOuter, { borderColor: c.surfaceVariant }]}>
          <View style={[styles.ringProgress, {
            borderColor: progressColor,
            borderTopColor: progressColor,
            borderRightColor: progress > 0.25 ? progressColor : 'transparent',
            borderBottomColor: progress > 0.5 ? progressColor : 'transparent',
            borderLeftColor: progress > 0.75 ? progressColor : 'transparent',
            transform: [{ rotate: `${-90 + progress * 360}deg` }],
          }]} />
          <View style={styles.ringInner}>
            <Text style={[styles.timerText, { color: remaining === 0 ? c.success : c.text }]}>
              {formatTime(remaining)}
            </Text>
            {remaining === 0 && (
              <Text style={[styles.doneText, { color: c.success }]}>REST COMPLETE</Text>
            )}
          </View>
          {/* "GET IT!" flash overlay */}
          {showGetIt && (
            <Animated.View style={[styles.getItOverlay, { opacity: getItAnim }]}>
              <Text style={styles.getItText}>GET IT! ⚡</Text>
            </Animated.View>
          )}
        </View>
      </View>

      {/* Controls */}
      <View style={styles.controls}>
        <TouchableOpacity
          style={[styles.controlBtn, { backgroundColor: c.surfaceVariant }]}
          onPress={handleReset}
        >
          <RotateCcw size={20} color={c.textSecondary} />
        </TouchableOpacity>

        <TouchableOpacity
          style={[styles.playBtn, { backgroundColor: isRunning ? c.warning : c.accent }]}
          onPress={isRunning ? handlePause : handleStart}
        >
          {isRunning ? <Pause size={24} color={c.accentForeground} /> : <Play size={24} color={c.accentForeground} />}
        </TouchableOpacity>

        <TouchableOpacity
          style={[styles.controlBtn, { backgroundColor: c.surfaceVariant }]}
          onPress={onDismiss}
        >
          <X size={20} color={c.textSecondary} />
        </TouchableOpacity>
      </View>

      {/* Quick-select times */}
      <View style={styles.quickTimes}>
        {QUICK_TIMES.map(secs => (
          <TouchableOpacity
            key={secs}
            style={[styles.quickBtn, {
              backgroundColor: totalSeconds === secs ? c.accent + '25' : c.surfaceVariant,
              borderColor: totalSeconds === secs ? c.accent : c.border,
            }]}
            onPress={() => handleSetTime(secs)}
          >
            <Text style={[styles.quickBtnText, {
              color: totalSeconds === secs ? c.accent : c.textSecondary,
            }]}>
              {secs >= 60 ? `${secs / 60}m` : `${secs}s`}
            </Text>
          </TouchableOpacity>
        ))}
      </View>
    </Animated.View>
    </View>
    </Modal>
  );
}

const styles = StyleSheet.create({
  overlay: {
    flex: 1,
    justifyContent: 'flex-end',
    paddingHorizontal: 16,
    paddingBottom: 100, // above tab bar
  },
  container: {
    borderWidth: 1,
    borderRadius: 2,
    padding: 20,
    gap: 16,
  },
  header: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
  },
  headerBtn: {
    padding: 4,
  },
  title: {
    fontSize: 15,
    fontWeight: '700',
  },
  timerDisplay: {
    alignItems: 'center',
  },
  ringOuter: {
    width: 140,
    height: 140,
    borderRadius: 4,
    borderWidth: 6,
    alignItems: 'center',
    justifyContent: 'center',
  },
  ringProgress: {
    position: 'absolute',
    width: 140,
    height: 140,
    borderRadius: 4,
    borderWidth: 6,
  },
  ringInner: {
    alignItems: 'center',
  },
  timerText: {
    fontSize: 36,
    fontWeight: '900',
    letterSpacing: -1,
  },
  doneText: {
    fontSize: 14,
    fontWeight: '800',
    letterSpacing: 1,
    marginTop: 2,
  },
  getItOverlay: {
    position: 'absolute',
    top: 0, left: 0, right: 0, bottom: 0,
    alignItems: 'center',
    justifyContent: 'center',
    borderRadius: 999,
    backgroundColor: 'rgba(3,3,3,0.75)',
  },
  getItText: {
    fontSize: 28,
    fontWeight: '900',
    color: '#B8972E',
    letterSpacing: 2,
  },
  controls: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    gap: 16,
  },
  controlBtn: {
    width: 44,
    height: 44,
    borderRadius: 0,
    alignItems: 'center',
    justifyContent: 'center',
  },
  playBtn: {
    width: 56,
    height: 56,
    borderRadius: 0,
    alignItems: 'center',
    justifyContent: 'center',
  },
  quickTimes: {
    flexDirection: 'row',
    justifyContent: 'center',
    gap: 6,
    flexWrap: 'wrap',
  },
  quickBtn: {
    paddingVertical: 6,
    paddingHorizontal: 14,
    borderRadius: 0,
    borderWidth: 1,
  },
  quickBtnText: {
    fontSize: 12,
    fontWeight: '700',
  },
  minimizedOverlay: {
    flex: 1,
    justifyContent: 'flex-end',
    alignItems: 'flex-end',
    paddingBottom: 110,
    paddingRight: 20,
  },
  minimizedPill: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 6,
    paddingVertical: 8,
    paddingHorizontal: 14,
    borderRadius: 2,
    borderWidth: 1,
    elevation: 5,
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 2 },
    shadowOpacity: 0.3,
    shadowRadius: 4,
  },
  minimizedText: {
    fontSize: 14,
    fontWeight: '800',
  },
});
